import { finleyAssert } from '@finley-technologies/common';
import { Funding } from '@finley-technologies/finley-types';

import {
  BaseBenchmarkPricingOptionFormData,
  BaseFixedPricingOptionFormData,
} from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/pricing-options/types';
import { CreditAgreementFullArtifact } from '~/core/routes/settings/implementations/common/types';

// constants for date formulas until we change BaseRelativeDateConfig to RelativeConfig. - https://linear.app/finleycms/issue/ENG-15050/update-baserelativeconfig-to-relativeconfig-for-pricing-options
const DEFAULT_START_DATE_FORMULA = 'EOMONTH(triggerDate, -1)';
const DEFAULT_END_DATE_FORMULA = 'EOMONTH(triggerDate, 0) + 5';

export const getBenchmarkFormValuesByPricingOption = (
  pricingOption: Extract<
    Funding.LoanMechanics['pricingOptions'][number],
    { type: 'benchmark' }
  >,
) => {
  const spread = pricingOption.adjustments?.find((a) => a.type === 'adjustment')
    ?.value;
  const floor = pricingOption.adjustments?.find((a) => a.type === 'floor')
    ?.value;
  const ceiling = pricingOption.adjustments?.find((a) => a.type === 'ceiling')
    ?.value;

  const result: BaseBenchmarkPricingOptionFormData = {
    type: 'benchmark',
    key: pricingOption.key,
    name: pricingOption.name,
    benchmark: pricingOption.benchmark,
    floor: floor ?? null,
    ceiling: ceiling ?? null,
    spread: spread ?? null,
    margin: pricingOption.margin.value,
    determination: pricingOption.determination,
    changeType: pricingOption.change.type,
    relativeRecurringDateConfig: {
      dateConfig: {
        relativeRecurringDateConfig:
          pricingOption.change.type === 'recurring'
            ? [
                {
                  startDateFormula: DEFAULT_START_DATE_FORMULA, // todo: waiting to change type from BaseRelativeDateConfig -> RelativeConfig. @sachinr4.
                  endDateFormula: DEFAULT_END_DATE_FORMULA,
                  baseDateConfig: pricingOption.change.dateConfig,
                },
              ]
            : [],
        exclusions: [],
      },
    },
  };

  return result;
};

export const getBenchmarkFormValues = (
  creditAgreement: CreditAgreementFullArtifact,
  pricingOptionId: string,
): BaseBenchmarkPricingOptionFormData => {
  const pricingOption = creditAgreement.loanMechanicsV1?.pricingOptions?.find(
    (pricingOptionItem) => pricingOptionItem.key === pricingOptionId,
  );

  finleyAssert(
    pricingOption,
    `Benchmark pricing option not found [id: ${pricingOptionId}]`,
  );
  finleyAssert(
    pricingOption.type === 'benchmark',
    `Benchmark type not found for pricing option [id: ${pricingOptionId}]. Instead found [type: ${pricingOption.type}]`,
  );

  return getBenchmarkFormValuesByPricingOption(pricingOption);
};

export const getFixedFormValuesByPricingOption = (
  pricingOption: Extract<
    Funding.LoanMechanics['pricingOptions'][number],
    { type: 'fixed' }
  >,
) => {
  const result: BaseFixedPricingOptionFormData = {
    type: 'fixed',
    key: pricingOption.key,
    name: pricingOption.name,
    value: pricingOption.value,
  };

  return result;
};

export const getFixedFormValues = (
  creditAgreement: CreditAgreementFullArtifact,
  pricingOptionId: string,
): BaseFixedPricingOptionFormData => {
  const pricingOption = creditAgreement?.loanMechanicsV1?.pricingOptions?.find(
    (pricingOptionItem) => pricingOptionItem.key === pricingOptionId,
  );

  finleyAssert(
    pricingOption,
    `Pricing option [id: ${pricingOptionId}] not found`,
  );
  finleyAssert(
    pricingOption.type === 'fixed',
    `Pricing option [id: ${pricingOptionId}] was expecting fixed rate type, but found [rate: ${pricingOption.type}]`,
  );

  return getFixedFormValuesByPricingOption(pricingOption);
};
